import { useState, useEffect, useCallback } from 'react';

export interface CookiePreferences {
  necessary: boolean; // Always true
  analytics: boolean;
}

export interface ConsentState {
  hasConsented: boolean;
  preferences: CookiePreferences;
  consentDate: string | null;
}

const CONSENT_KEY = 'smartsite_cookie_consent';

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
};

const defaultState: ConsentState = {
  hasConsented: false,
  preferences: defaultPreferences,
  consentDate: null,
};

export function useCookieConsent() {
  const [consentState, setConsentState] = useState<ConsentState>(defaultState);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (stored) {
        const parsed: ConsentState = JSON.parse(stored);
        setConsentState(parsed);
        setShowBanner(false);
      } else {
        // No consent stored, show banner
        setShowBanner(true);
      }
    } catch {
      setShowBanner(true);
    }
  }, []);

  // Save consent to localStorage
  const saveConsent = useCallback((preferences: CookiePreferences) => {
    const newState: ConsentState = {
      hasConsented: true,
      preferences: {
        ...preferences,
        necessary: true, // Always true
      },
      consentDate: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(newState));
    setConsentState(newState);
    setShowBanner(false);
    setShowPreferencesModal(false);
  }, []);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    saveConsent({ necessary: true, analytics: true });
  }, [saveConsent]);

  // Reject all non-essential cookies
  const rejectAll = useCallback(() => {
    saveConsent({ necessary: true, analytics: false });
  }, [saveConsent]);

  // Save custom preferences
  const savePreferences = useCallback((preferences: CookiePreferences) => {
    saveConsent(preferences);
  }, [saveConsent]);

  // Open preferences modal
  const openPreferences = useCallback(() => {
    setShowPreferencesModal(true);
    setShowBanner(false);
  }, []);

  // Close preferences modal
  const closePreferences = useCallback(() => {
    setShowPreferencesModal(false);
    // If hasn't consented yet, show banner again
    if (!consentState.hasConsented) {
      setShowBanner(true);
    }
  }, [consentState.hasConsented]);

  // Reset consent (for testing or user request)
  const resetConsent = useCallback(() => {
    localStorage.removeItem(CONSENT_KEY);
    setConsentState(defaultState);
    setShowBanner(true);
  }, []);

  // Check if analytics is allowed
  const isAnalyticsAllowed = consentState.hasConsented && consentState.preferences.analytics;

  return {
    consentState,
    showBanner,
    showPreferencesModal,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    closePreferences,
    resetConsent,
    isAnalyticsAllowed,
  };
}
